import type React from 'react';

import { Separator, Spinner, Text } from '@change/ds-core-ui';
import { CheckmarkVerifiedIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { useSignFormStep } from '#src/app/pages/petitionDetails/shared/hooks';
import {
	SIGN_FORM_STEP_HEADING_FOCUS_TARGET,
	signFormStepFocusFallback,
} from '#src/app/pages/petitionDetails/shared/utilities';
import { useFocusFallbackRef } from '#src/app/shared/hooks/focus';

import { SignFormContainer } from '../signForm';
import { SignatureStatsActiveContainer } from '../stats/signatureStats';

const SideUnsignedContainerInner: React.FC<{ signFormStep: number }> = ({ signFormStep }) => {
	const focusFallbackRef = useFocusFallbackRef(signFormStepFocusFallback);

	return (
		<div ref={focusFallbackRef} className="contents">
			<div className={cn('flex items-center gap-2', signFormStep === 2 ? 'mb-2' : 'my-4')}>
				{signFormStep === 2 && (
					<CheckmarkVerifiedIcon data-testid="title-checkmark-icon" className="text-icon-information" />
				)}

				<Text variant="heading" as="h2" size="small" data-focus-target={SIGN_FORM_STEP_HEADING_FOCUS_TARGET}>
					{signFormStep === 1 ? (
						<Translate value="corgi.petition_gamma.petition_sign.sign_title" />
					) : (
						<Translate value="corgi.petition_gamma.petition_sign.address.verify_title" />
					)}
				</Text>
			</div>

			{signFormStep === 2 && (
				<div className="mb-4">
					<Text variant="body" size="small">
						<Translate value="corgi.petition_gamma.petition_sign.address.disclaimer" />
					</Text>
				</div>
			)}

			<div className="hidden md:block">
				<SignFormContainer dataQa="sign-form-desktop" />
			</div>

			<div className="block md:hidden">
				<Spinner className="mx-auto mt-16 size-6" />
			</div>
		</div>
	);
};

export const SideUnsignedContainer: React.FC<EmptyProps> = () => {
	const { signFormStep } = useSignFormStep();

	return (
		<>
			{signFormStep === 1 && (
				<>
					<div className="mb-4">
						<SignatureStatsActiveContainer />
					</div>
					<Separator />
				</>
			)}

			{/* using programmatic media query to allow lazy loading of the sign form in modal on mobile */}
			{/* Spinner is necessary for SSR */}
			<ClientRender placeholder={<Spinner className="mx-auto mt-16 size-6" />}>
				<SideUnsignedContainerInner signFormStep={signFormStep} />
			</ClientRender>
		</>
	);
};
